import { useMemo } from "react";
import { AxiosError } from "axios";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { createDashboardLayoutQueryKey } from "@/features/dashboard/hooks/useDashboardLayout";
import { getDashboardLayout } from "@/features/dashboard/services/dashboardLayout.service";
import { getUnifiedTheme } from "@/features/theme/services/brandTheme.service";
import type { BrandThemeData, BrandThemeSource } from "@/features/theme/types/brandTheme.type";
import { buildFallbackTheme } from "@/features/theme/utils/buildFallbackTheme";

const FIVE_MINUTES_MS = 5 * 60 * 1000;

type UseBrandThemeQueryReturn = {
  brandTheme: BrandThemeData | null
  isLoading: boolean
  isError: boolean
  source: BrandThemeSource
};

const isNotFoundError = (error: Error | null): boolean => {
  if (!error) return false;
  return error instanceof AxiosError && error.response?.status === 404;
};

/**
 * Fetches the unified brand theme from GET /theme.
 * Falls back to composing theme from organization + dashboard_layout when the endpoint returns 404 or 5xx.
 * Returns isError: true on 5xx fallback so consumers can show a degraded-state warning.
 */
export const useBrandThemeQuery = (): UseBrandThemeQueryReturn => {
  const { isAuthenticated, organization } = useAuth();
  const organizationId = organization?.id;

  const unifiedQuery = useQuery({
    queryKey: ["brand-theme", organizationId ?? "anonymous"],
    queryFn: getUnifiedTheme,
    staleTime: FIVE_MINUTES_MS,
    gcTime: FIVE_MINUTES_MS * 2,
    retry: (failureCount, error) => {
      if (isNotFoundError(error)) return false;
      return failureCount < 2;
    },
    enabled: isAuthenticated && !!organizationId,
  });

  const needsFallback = unifiedQuery.isError;

  const dashboardLayoutQuery = useQuery({
    queryKey: createDashboardLayoutQueryKey(organizationId),
    queryFn: getDashboardLayout,
    staleTime: FIVE_MINUTES_MS,
    gcTime: FIVE_MINUTES_MS * 2,
    retry: 2,
    enabled: needsFallback && isAuthenticated && !!organizationId,
  });

  return useMemo((): UseBrandThemeQueryReturn => {
    if (!isAuthenticated || !organizationId) {
      return {
        brandTheme: null, isLoading: false, isError: false, source: "pending",
      };
    }

    // Unified endpoint succeeded
    if (unifiedQuery.isSuccess && unifiedQuery.data) {
      const data = unifiedQuery.data;
      return {
        brandTheme: {
          colors: data.colors,
          banners: data.banners,
          vouchers: data.vouchers,
          navigationPosition: data.navigation_position,
          dashboardLayout: data.dashboard_layout,
          menu: data.menu ?? null,
        },
        isLoading: false,
        isError: false,
        source: "unified",
      };
    }

    // Unified returned error (404 or 5xx) → use fallback
    if (needsFallback) {
      const isStillLoading = dashboardLayoutQuery.isLoading;
      const isServerError = !isNotFoundError(unifiedQuery.error);

      return {
        brandTheme: buildFallbackTheme(organization, dashboardLayoutQuery.data),
        isLoading: isStillLoading,
        isError: isServerError || dashboardLayoutQuery.isError,
        source: isServerError ? "fallback-error" : "fallback",
      };
    }

    // Still loading unified query
    return {
      brandTheme: null, isLoading: true, isError: false, source: "pending",
    };
  }, [
    isAuthenticated,
    organizationId,
    unifiedQuery.isSuccess,
    unifiedQuery.data,
    unifiedQuery.isError,
    unifiedQuery.error,
    needsFallback,
    dashboardLayoutQuery.isLoading,
    dashboardLayoutQuery.isError,
    dashboardLayoutQuery.data,
    organization,
  ]);
};
